import React, {
    forwardRef,
    ReactNode,
    RefObject,
    useCallback,
    useContext,
    useEffect,
    useImperativeHandle,
    useRef,
    useState,
} from 'react'
import { MAX_OUTPUT_GIF_DURATION_FOR_CAPTIONS } from 'site/creation-tools/constants'
import { CreationToolFrame } from 'site/creation-tools/types'
import CreationToolOutputType from 'site/creation-tools/types/output-type'
import { drawCaptionsIntoFrames } from 'site/creation-tools/utils/draw-frames'
import getSourceFrames from 'site/creation-tools/utils/get-source-frames'
import styled from 'styled-components'
import { useCreationDrawLoop } from '../../hooks/use-creation-draw-loop'
import { CreationSourceEditorContext } from '../source-editor/manager'
import { CreationToolsArtboardContext } from './context'
import { ArtboardCropPosition, ArtboardRef } from './types'

const Artboard = styled.div`
    overflow: hidden;
    position: relative;

    canvas {
        display: block;
        height: 100%;
        width: 100%;
    }
`

type Props = {
    className?: string
    canvasScale?: number
    children?: ReactNode
    fps?: number
    crop?: ArtboardCropPosition
    height: number
    width: number
}

const CreationToolsArtboard = (
    { className, height, width, children, canvasScale = 1, fps, crop: defaultCrop }: Props,
    ref: RefObject<ArtboardRef>
) => {
    const { source, captions, outputType } = useContext(CreationSourceEditorContext)
    const canvasRef = useRef<HTMLCanvasElement>(null)
    const [renderingContext, setRenderingContext] = useState<CanvasRenderingContext2D | null>(null)
    const [crop, setCrop] = useState<ArtboardCropPosition | undefined>(defaultCrop)
    const [draggingCaptionId, setDraggingCaptionId] = useState<string>()
    const [contextMenuCaptionId, setContextMenuCaptionId] = useState<string>()
    const frameCount = useCreationDrawLoop(fps)

    const generateCanvasWithOneFrame = useCallback(() => {
        if (!canvasRef.current) return []

        const canvas = document.createElement('canvas')
        const context = canvas.getContext('2d')
        const x = crop?.x || 0
        const y = crop?.y || 0

        canvas.width = crop?.width || canvasRef.current.width
        canvas.height = crop?.height || canvasRef.current.height
        context?.drawImage(canvasRef.current, -x, -y)

        return [{ canvas }]
    }, [canvasRef, crop])

    const generateCanvasForMultipleFrames = useCallback(async () => {
        let originalFrames: CreationToolFrame[] = source.frames

        if (!originalFrames.length) {
            originalFrames = await getSourceFrames({ sourceFile: source, ignoreCache: true })
        }

        const finalFrames: CreationToolFrame[] = []
        const x = crop?.x || 0
        const y = crop?.y || 0

        for (const originalFrame of originalFrames) {
            const canvas = document.createElement('canvas')
            const context = canvas.getContext('2d')
            canvas.width = crop?.width || originalFrame.canvas.width
            canvas.height = crop?.height || originalFrame.canvas.height
            context?.drawImage(originalFrame.canvas, -x, -y)
            finalFrames.push({ canvas })
        }

        const maxWidth = ((crop?.width || source.width) * 0.9) / canvasScale

        if (captions.length > 0) {
            await drawCaptionsIntoFrames({
                frames: finalFrames,
                captions,
                maxWidth,
                cropX: x,
                cropY: y,
                cropHeight: crop?.height || source.height,
            })
        }

        return finalFrames
    }, [source, crop, captions])

    // export ref functions
    useImperativeHandle(ref, () => ({
        getFrames: async () => {
            if (!canvasRef.current) return []

            if (outputType === CreationToolOutputType.Video || source.duration > MAX_OUTPUT_GIF_DURATION_FOR_CAPTIONS) {
                return generateCanvasWithOneFrame()
            } else if (outputType === CreationToolOutputType.Gif || outputType === CreationToolOutputType.Sticker) {
                return generateCanvasForMultipleFrames()
            }

            return []
        },
    }))

    // set the rendering context
    useEffect(() => {
        if (canvasRef.current) {
            setRenderingContext(canvasRef.current.getContext('2d'))
        }
    }, [canvasRef, setRenderingContext])

    // clear canvas every re-render
    if (renderingContext) {
        renderingContext.canvas.width = width * canvasScale
        renderingContext.canvas.height = height * canvasScale
    }

    return (
        <CreationToolsArtboardContext.Provider
            value={{
                frameCount,
                renderingContext,
                canvasScale,
                crop,
                setCrop,
                draggingCaptionId,
                setDraggingCaptionId,
                contextMenuCaptionId,
                setContextMenuCaptionId,
            }}
        >
            <Artboard className={className} style={{ width, height }}>
                <canvas ref={canvasRef} />
                {children}
            </Artboard>
        </CreationToolsArtboardContext.Provider>
    )
}

export default forwardRef(CreationToolsArtboard)
